import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { usePostHog } from 'posthog-js/react'

import { store } from '../store/store';
import { selectUserId } from '../store/userSlice';
import { setSessionData, UserMode } from "../store/sessionSlice";
import { getDecryptedJwt } from "../utils/tokenEncryptionUtils";
import {
  SessionSetupFlows,
  TokenSetupResult,
  SessionCreatedCallback,
  SessionSetupResult,
} from "../utils/sessionSetupFlows";
import { SessionSpec } from "../utils/sessionEndpoints";

// Re-export the SessionSetupResult type from the service
export type { TokenSetupResult } from "../utils/sessionSetupFlows";


/**
 * Hook that provides methods to set up streamer connections.
 * These methods:
 * 1. call corresponding StreamerSetupFlows methods, and
 * 2. dispatch the results to the Redux store.
 */
export function useSessionSetupFlow() {
  const dispatch = useDispatch();
  const posthog = usePostHog();

  const handleManualSessionSetup = useCallback(
    async (
      sessionSpec: SessionSpec,
      sessionCreatedCallback?: SessionCreatedCallback,
      abortSignal?: AbortSignal
    ): Promise<string> => {

      const userId = selectUserId(store.getState());

      console.log("▶ handleManualSessionSetup start, userId:", userId, " sessionSpec:", sessionSpec);

      if (!userId) {
        throw new Error("handleManualSessionSetup: Missing userId");
      }

      const token = getDecryptedJwt();

      console.log("▶ handleManualSessionSetup using jwt token:", token?.substring(0, 20) + "...");

      const getError = (msg: string) => {
        console.log("▶ handleManualSessionSetup failed:", msg);
        posthog?.capture('session_setup_error', { method: 'manual', error: msg });
        return new Error(`handleManualSessionSetup failed: ${msg}`);
      };

      const t0 = performance.now();
      let result: SessionSetupResult;
      try {
        result = await SessionSetupFlows.directPixelStreamingFlow(
          sessionSpec,
          token,
          sessionCreatedCallback,
          abortSignal
        );
      } catch (error) {
        throw getError((error as Error).message);
      }

      console.log("▶ handleManualSessionSetup result:", result);

      if (!result) {
        throw getError("No result from directPixelStreamingFlow");
      }

      if (!result.sessionId) {
        throw getError("No session id from directPixelStreamingFlow");
      }

      const t1 = performance.now();
      const startTimeSeconds = (t1 - t0) / 1000.0;
      console.log("▶ handleManualSessionSetup duration:", startTimeSeconds, "s");
      posthog?.capture("session_setup_succeeded", { startTimeSeconds, sessionId: result.sessionId });

      dispatch(
        setSessionData({
          apiSession: result.apiSession,
          connection: {
            websocketUrl: result.websocketUrl,
            baseUrl: result.baseUrl,
            sessionSpec,
            sessionId: result.sessionId,
            streamToken: undefined,
          },
          conversation: {
            id: result.conversationId,
            userIsHost: false, //true,
            mode: UserMode.SingleUser,
          },
          streaming: {
            pixelStreamerURL: result.pixelStreamerURL,
            tokenMicEnabled: true,
          },
          user: {
            userId: userId,
          },
        })
      );

      return result.sessionId;
    },
    [dispatch]
  );

  // Handle token-based streamer setup flow
  const handleTokenSessionSetup = useCallback(
    async (joinToken: string): Promise<TokenSetupResult> => {
      console.log("▶ handleTokenSessionSetup start, joinToken:", joinToken);

      const result = await SessionSetupFlows.tokenFlow(joinToken);

      console.log("▶ handleTokenSessionSetup result:", result);

      dispatch(
        setSessionData({
          connection: {
            websocketUrl: result.websocketUrl,
            baseUrl: result.baseUrl,
            //sessionSpec: undefined,
            //sessionId: undefined,
            //streamToken: undefined,
          },
          conversation: {
            id: result.conversationId,
            userIsHost: result.userIsHost,
            mode: result.userIsHost ? UserMode.Host : UserMode.Guest,
          },
          streaming: {
            pixelStreamerURL: result.pixelStreamerURL,
            tokenMicEnabled: result.tokenMicEnabled,
          },
          user: {
            userId: result.userId,
          },
        })
      );

      // Return with overridden pixelStreamerURL if provided
      return {
        ...result,
        pixelStreamerURL: result.pixelStreamerURL,
      };
    },
    [dispatch]
  );

  return {
    handleTokenSessionSetup,
    handleManualSessionSetup,
  };
}
